/**
 * Creates a tab control.
 *
 * Can use API or data attributes. Automatically applies all aria attributes on init.
 *
 * data-tabs="{json options}"
 *
 * If the only option you need to set is the contentContainer, that can be passed as a string. data-tabs="tab-container-id-here"
 *
 * Currently this requires the tabs be setup in ul > li > a format.
 *
 * Options:
 * contentContainer   ID of tab content container (required)
 * defaultTabIndex    Default is 0
 * tabSelector        Default is 'a'. Must be an A tag. Selector string for querySelectorAll().
 *
 * # Troubleshooting
 * ## Flash of tab content
 * Add 'hidden' to all tab panels except the default
 *
 */

import {keys} from './input.js';

// console.log('Site:tabs');

const instances = [];
const defaults = {
  tabSelector     : 'a',
  defaultTabIndex : 0,
};


export class Tabs {
  options;
  tabContainer;
  contentContainer;
  tabs = [];


  /**
   * Constructs a new Tab control instance.
   *
   * @param {object} options The options.
   *
   * ```Options:
   *   tabSelector
   *   defaultTabIndex
   *   tabContainer     DOMNode
   *   contentContainer DOMNode
   * ```
   */
  constructor(options) {
    // console.log('Tabs:constructor');

    instances.push(this);

    this.options = {...defaults, ...options};

    this.tabContainer = this.options.tabContainer;
    this.contentContainer = this.options.contentContainer;

    delete this.options.tabContainer;
    delete this.options.contentContainer;

    this.init();
  }


  init() {
    // console.log('Tabs:init');

    this.tabContainer.setAttribute('role', 'tablist');
    this.tabContainer.addEventListener('click', this.onTabClick.bind(this));
    this.tabContainer.addEventListener('keydown', this.onKeyDown.bind(this));

    this.refresh();
    this.select(this.options.defaultTabIndex);
  }


  // Call this if the number of tabs changes
  refresh() {
    // console.log('Tabs:refresh');

    this.tabs = [];

    this.tabContainer.querySelectorAll(this.options.tabSelector).forEach(function (tabElement) {
      const tabPanelId = tabElement.getAttribute('href').slice(1);
      const tabId = tabElement.getAttribute('id') || 'tab-' + tabPanelId; // Each tab requires an id, if one isn't set, create using the panel id
      const panelElement = document.getElementById(tabPanelId);

      tabElement.closest('li').setAttribute('role', 'presentation'); // Required or the SR will read 'tab 1 of 1' instead of 'tab 1 of 4'
      tabElement.setAttribute('id', tabId);
      tabElement.setAttribute('role', 'tab');
      tabElement.setAttribute('aria-controls', tabPanelId);
      panelElement.setAttribute('role', 'tabpanel');
      panelElement.setAttribute('aria-labelledby', tabId);
      panelElement.setAttribute('tabindex', 0);

      this.tabs.push({
        tabElement,
        panelElement,
      });
    }.bind(this));
  }


  onTabClick(event) {
    // console.log('Tabs:onTabClick');

    const element = event.target;

    for (let i = this.tabs.length - 1; i >= 0; i--) {
      if (this.tabs[i].tabElement === element) {
        this.select(i);
        event.preventDefault();
        break;
      }
    }
  }


  onKeyDown(event) {
    // console.log('Tabs:onKeyDown');

    switch (event.keyCode) {
      case keys.ARROW_LEFT:
        this.selectPrevious();
        break;

      case keys.ARROW_RIGHT:
        this.selectNext();
        break;

      case keys.HOME:
        this.selectFirst();
        break;

      case keys.END:
        this.selectLast();
        break;

      default:
        return;
    }

    event.preventDefault();
  }


  selectPrevious() {
    // console.log('Tabs:selectPrevious');

    const currentIndex = this.getCurrentTabIndex();

    if (currentIndex - 1 >= 0) {
      this.select(currentIndex - 1);
    }
  }


  selectNext() {
    // console.log('Tabs:selectNext');

    const currentIndex = this.getCurrentTabIndex();
    const tabCount = this.tabs.length;

    if (currentIndex < tabCount - 1) {
      this.select(currentIndex + 1);
    }
  }


  selectFirst() {
    // console.log('Tabs:selectFirst');

    this.select(0);
  }


  selectLast() {
    // console.log('Tabs:selectLast');

    this.select(this.tabs.length - 1);
  }


  select(index) {
    // console.log('Tabs:select', index);

    if (index < 0 || index > this.tabs.length - 1) {
      return;
    }

    try {
      if (index === this.getCurrentTabIndex()) {
        return;
      }
    } catch (e) {
      // console.log('e', e);
    }

    for (let i = this.tabs.length - 1; i >= 0; i--) {
      const {
        tabElement,
        panelElement,
      } = this.tabs[i];
      const active = i === index;

      tabElement.setAttribute('tabindex', active ? 0 : -1);
      tabElement.setAttribute('aria-selected', active);

      if (active) {
        tabElement.focus();
        panelElement.removeAttribute('hidden');
      } else {
        panelElement.setAttribute('hidden', true);
      }
    }
  }


  /**
   * Gets the current tab index.
   *
   * @throws Error.
   *
   * @return {number} The current tab index.
   */
  getCurrentTabIndex() {
    // console.log('Tabs:getCurrentTabIndex');

    for (let i = this.tabs.length - 1; i >= 0; i--) {
      if (this.tabs[i].tabElement.getAttribute('aria-selected') === 'true') {
        return i;
      }
    }

    throw new Error('Tabs: No tab selected');
  }
}


document.querySelectorAll('[data-tabs]').forEach(function (element) {
  const data = element.dataset.tabs;

  let options = {};

  if (data && data[0] === '{') {
    try {
      options = JSON.parse(data);

      if (typeof options !== 'object') {
        throw new Error('Tabs: Not an object');
      }
    } catch (e) {
      // console.log('e', e);
      options = {};
    }
  } else {
    options.contentContainer = data;
  }

  if (!options.contentContainer) {
    console.log('Tabs: No tab container specified');

    return;
  }

  options.contentContainer = document.getElementById(options.contentContainer);

  if (!options.contentContainer) {
    console.log('Tabs: Tab container element not found');

    return;
  }

  options.tabContainer = element;

  // console.log('options', options);

  /* eslint-disable-next-line */
  new Tabs(options);
});
